import React, { useMemo, useRef, useState } from 'react';
import useElementWidth from './useElementWidth';
import Tooltip from './Tooltip';
import { niceTicks, isNum } from '../format';

const M = { top: 12, right: 8, bottom: 30, left: 8 };
const MAX_BAR = 24;

function roundedTopRect(x, y, w, h, r) {
  if (h <= 0 || w <= 0) return '';
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + rr} a${rr},${rr} 0 0 1 ${rr},-${rr} h${w - 2 * rr} a${rr},${rr} 0 0 1 ${rr},${rr} v${h - rr} h${-w} z`;
}

/**
 * Column chart for distributions. One series (ordinal buckets) or a small
 * grouped set. `reference` draws a gray tick per category (e.g. national share).
 * series: [{ id, label, color, values: [number|null] }]
 */
export default function BarChart({
  categories = [],
  series = [],
  reference = null,
  height = 200,
  yFormat = (v) => String(v),
  valueFormat,
  labelRotate = false,
}) {
  const wrapRef = useRef(null);
  const width = useElementWidth(wrapRef);
  const [hover, setHover] = useState(null);

  const model = useMemo(() => {
    const n = categories.length;
    if (!n || !series.length) return null;
    let yMax = 0;
    series.forEach((s) => s.values.forEach((v) => { if (isNum(v)) yMax = Math.max(yMax, v); }));
    if (reference) reference.values.forEach((v) => { if (isNum(v)) yMax = Math.max(yMax, v); });
    const ticks = niceTicks(0, yMax || 1, 4);
    const y1 = ticks[ticks.length - 1];
    const labelWidth = Math.max(...ticks.map((t) => yFormat(t).length), 2) * 7 + 8;
    const left = M.left + labelWidth;
    const plotW = Math.max(40, width - left - M.right);
    const bottom = labelRotate ? 48 : M.bottom;
    const plotH = Math.max(40, height - M.top - bottom);
    const band = plotW / n;
    const groupW = Math.min(band * 0.72, MAX_BAR * series.length + 2 * (series.length - 1));
    const barW = (groupW - 2 * (series.length - 1)) / series.length;
    const sy = (v) => M.top + plotH - (v / (y1 || 1)) * plotH;
    const labelEvery = Math.max(1, Math.ceil((n * 44) / plotW));
    return { n, ticks, left, plotW, plotH, band, groupW, barW, sy, y1, labelEvery };
  }, [categories, series, reference, width, height, yFormat, labelRotate]);

  if (!model) return <div ref={wrapRef} className="le-chart-empty" style={{ height }}>No data to plot</div>;

  const fmtVal = valueFormat || yFormat;
  const hoverRows = hover !== null ? [
    ...series.map((s) => ({ id: s.id, label: s.label, color: s.color, value: isNum(s.values[hover]) ? fmtVal(s.values[hover]) : '—' })),
    ...(reference ? [{ id: 'ref', label: reference.label, value: isNum(reference.values[hover]) ? fmtVal(reference.values[hover]) : '—' }] : []),
  ] : [];

  return (
    <div ref={wrapRef} className="le-chart le-bar-chart" style={{ height }} onPointerLeave={() => setHover(null)}>
      <svg width={width} height={height} role="img" aria-label="Bar chart">
        {model.ticks.map((t) => (
          <g key={t}>
            <line className="le-grid" x1={model.left} x2={model.left + model.plotW} y1={model.sy(t)} y2={model.sy(t)} />
            <text className="le-axis-text" x={model.left - 6} y={model.sy(t) + 4} textAnchor="end">{yFormat(t)}</text>
          </g>
        ))}
        <line className="le-axis" x1={model.left} x2={model.left + model.plotW} y1={M.top + model.plotH} y2={M.top + model.plotH} />
        {categories.map((cat, i) => {
          const bx = model.left + i * model.band;
          const gx = bx + (model.band - model.groupW) / 2;
          const active = hover === i;
          return (
            <g key={cat} className={active ? 'le-bar-group active' : 'le-bar-group'}>
              <rect
                className="le-hit"
                x={bx}
                y={M.top}
                width={model.band}
                height={model.plotH}
                onPointerEnter={() => setHover(i)}
                onPointerMove={() => setHover(i)}
              />
              {series.map((s, si) => {
                const v = s.values[i];
                if (!isNum(v)) return null;
                const x = gx + si * (model.barW + 2);
                const y = model.sy(v);
                const h = M.top + model.plotH - y;
                return (
                  <path key={s.id} className="le-bar" d={roundedTopRect(x, y, model.barW, h, 4)} style={{ fill: s.color }} />
                );
              })}
              {reference && isNum(reference.values[i]) ? (
                <line
                  className="le-ref-tick"
                  x1={gx - 3}
                  x2={gx + model.groupW + 3}
                  y1={model.sy(reference.values[i])}
                  y2={model.sy(reference.values[i])}
                />
              ) : null}
              {i % model.labelEvery === 0 ? (
                <text
                  className="le-axis-text"
                  x={bx + model.band / 2}
                  y={height - 10}
                  textAnchor={labelRotate ? 'end' : 'middle'}
                  transform={labelRotate ? `rotate(-35 ${bx + model.band / 2} ${height - 10})` : undefined}
                >
                  {cat}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {hover !== null ? (
        <Tooltip
          x={model.left + hover * model.band + model.band / 2}
          y={M.top + 10}
          width={width}
          title={categories[hover]}
          rows={hoverRows}
        />
      ) : null}
    </div>
  );
}
